import {
  STORE_SIZE_CHECK_DEBOUNCE_MS,
  STORE_SIZE_WARNING_BYTES,
  STORE_SIZE_WARNING_REPEAT_THRESHOLD_BYTES,
} from "./limits";

import { rollbarAndLogWarning } from "utils/logger";

export type StoreApiOperation = "retrieve" | "list";

export type StoreApiCallLogEntry = {
  ts: number;
  model: string;
  operation: StoreApiOperation;
  params?: Record<string, unknown>;
  count: number;
  bytes: number;
};

export const storeApiCallLog: StoreApiCallLogEntry[] = [];

let getStoreState: (() => unknown) | null = null;
let nextTotalWarningAtBytes = STORE_SIZE_WARNING_BYTES;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

export function setStoreGetter(getter: () => unknown) {
  getStoreState = getter;
}

function bytesOfJson(data: unknown): number {
  try {
    const s = JSON.stringify(data);
    // More accurate UTF-8 byte length
    return new TextEncoder().encode(s).length;
  } catch {
    return 0;
  }
}

export function recordStoreApiCall(
  model: string,
  operation: StoreApiOperation,
  params: Record<string, unknown> | undefined,
  results: unknown,
) {
  const arrayResults: unknown[] = Array.isArray(results) ? (results as unknown[]) : [results];
  const count = arrayResults.length;
  const bytes = bytesOfJson(arrayResults);

  storeApiCallLog.push({ ts: Date.now(), model, operation, params, count, bytes });
}

export function getTotalStoreSizeBytes(): number {
  if (!getStoreState) return 0;
  const state = getStoreState();
  return bytesOfJson(state);
}

export function computeTopCalls(n = 10): string[] {
  return storeApiCallLog
    .toSorted((a, b) => b.bytes - a.bytes)
    .slice(0, n)
    .map(
      (call) => `${call.model}.${call.operation}(${JSON.stringify(call.params)})=${call.bytes}B`,
    );
}

function computePerSliceSizes(state: unknown): string[] {
  if (!state || typeof state !== "object") return [];
  const entries: { key: string; bytes: number }[] = [];
  for (const [key, value] of Object.entries(state as Record<string, unknown>)) {
    entries.push({ key, bytes: bytesOfJson(value) });
  }
  entries.sort((a, b) => b.bytes - a.bytes);
  return entries.map((entry) => `${entry.key}=${entry.bytes}B`);
}

function maybeWarn(totalBytes: number, state: unknown) {
  if (totalBytes < nextTotalWarningAtBytes) return;
  console.warn("Store total size (bytes)", totalBytes);

  const perSlice = computePerSliceSizes(state);
  const topCalls = computeTopCalls();

  rollbarAndLogWarning("Client store size exceeds threshold", {
    totalBytes,
    threshold: nextTotalWarningAtBytes,
    topSlice: perSlice[0],
    topCall: topCalls[0],
  });

  // Move the next threshold forward to avoid spamming warnings
  nextTotalWarningAtBytes = totalBytes + STORE_SIZE_WARNING_REPEAT_THRESHOLD_BYTES;
}

export function scheduleStoreSizeCheck() {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
  }
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    if (!getStoreState) return;
    const state = getStoreState();
    const totalBytes = bytesOfJson(state);
    maybeWarn(totalBytes, state);
  }, STORE_SIZE_CHECK_DEBOUNCE_MS);
}
